import { getCurrentUserId } from "@risalabs_frontend_org/auth-wrapper";
import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { ToastMessages } from "../../../../../components/toasts/toastMessages";
import { setOpenedModalId } from "../../../../../redux/slice/modalSlice";
import { AppDispatch } from "../../../../../redux/store/store";
import { killSwitch } from "../../../../../../shared/api/postCall/killSwitch";
import { ModalId } from "../../../../../../shared/enums/modalId";
import { NarAgentGridTableColumnKeys } from "../../../../../../shared/enums/tableColumnKeys";
import { getLatestRemarkText } from "../../../../../utils/getLatestNarAgentRemark";

export type ToggleSwitchData = {
  id: string;
  required: boolean;
  value: boolean;
} | null;

export type ToggleChangeData = {
  name: string;
  required: boolean;
  value: boolean;
  gridIdentifier?: string;
};

const updateToggleState = (
  setTableData: React.Dispatch<React.SetStateAction<any[]>>,
  data: { id: string; required: boolean; value: boolean },
  gridIdentifier:
    | NarAgentGridTableColumnKeys.AgentMode
    | NarAgentGridTableColumnKeys.AutopassReview,
) => {
  setTableData((prevData: any[]) =>
    prevData.map((row: any) => {
      if (
        gridIdentifier === NarAgentGridTableColumnKeys.AgentMode &&
        row[NarAgentGridTableColumnKeys.AgentMode]?.id === data.id
      ) {
        return {
          ...row,
          [NarAgentGridTableColumnKeys.AgentMode]: {
            ...row[NarAgentGridTableColumnKeys.AgentMode],
            defaultChecked: data.value,
            rowData: {
              ...row[NarAgentGridTableColumnKeys.AgentMode].rowData,
              agent: data.value,
            },
          },
        };
      }
      if (
        gridIdentifier === NarAgentGridTableColumnKeys.AutopassReview &&
        row[NarAgentGridTableColumnKeys.AutopassReview]?.id === data.id
      ) {
        return {
          ...row,
          [NarAgentGridTableColumnKeys.AutopassReview]: {
            ...row[NarAgentGridTableColumnKeys.AutopassReview],
            defaultChecked: data.value,
            rowData: {
              ...row[NarAgentGridTableColumnKeys.AutopassReview].rowData,
              autopass_review: data.value,
            },
          },
        };
      }
      return row;
    }),
  );
};

export function useNarAgentGridToggle(
  tableData: any[],
  setTableData: React.Dispatch<React.SetStateAction<any[]>>,
  clientId: string,
) {
  const dispatch = useDispatch<AppDispatch>();
  const [toggleSwitchData, setToggleSwitchData] =
    useState<ToggleSwitchData>(null);

  const handleToggleChange = async (data: ToggleChangeData) => {
    const mappedData = {
      id: data.name,
      required: data.required,
      value: data.value,
    };

    const gridIdentifier =
      data.gridIdentifier ?? NarAgentGridTableColumnKeys.AgentMode;

    switch (gridIdentifier) {
      case NarAgentGridTableColumnKeys.AutopassReview: {
        updateToggleState(
          setTableData,
          mappedData,
          NarAgentGridTableColumnKeys.AutopassReview,
        );
        const row = tableData.find(
          (r: any) =>
            r[NarAgentGridTableColumnKeys.AutopassReview]?.id === data.name,
        );
        const userId = getCurrentUserId();
        const existingRemarkText = getLatestRemarkText(
          row?.[NarAgentGridTableColumnKeys.AutopassReview]?.rowData?.remarks,
        );
        try {
          const payload = {
            grid_identifier: data.name,
            client_id: clientId,
            agent_mode:
              row?.[NarAgentGridTableColumnKeys.AgentMode]?.defaultChecked ??
              false,
            autopass_review: data.value,
            remarks: existingRemarkText === "NA" ? "" : existingRemarkText,
            user_id: userId ?? "",
          };
          const response = await killSwitch(payload);
          if (response.success) {
            toast.success(
              `Autopass review ${data.value ? "enabled" : "disabled"} successfully`,
            );
          } else {
            toast.error(
              `Failed to ${data.value ? "enable" : "disable"} autopass review`,
            );
            updateToggleState(
              setTableData,
              { ...mappedData, value: !data.value },
              NarAgentGridTableColumnKeys.AutopassReview,
            );
          }
        } catch {
          toast.error(ToastMessages.KILL_SWITCH_FAILURE);
          updateToggleState(
            setTableData,
            { ...mappedData, value: !data.value },
            NarAgentGridTableColumnKeys.AutopassReview,
          );
        }
        break;
      }
      case NarAgentGridTableColumnKeys.AgentMode:
      default:
        dispatch(
          setOpenedModalId({
            id: ModalId.TOGGLE_SWITCH_CONFIRMATION_DIALOG,
            metaData: {
              id: mappedData.id,
              switchState: data.value,
              rowData: mappedData,
            },
          }),
        );
        updateToggleState(
          setTableData,
          mappedData,
          NarAgentGridTableColumnKeys.AgentMode,
        );
        setToggleSwitchData(mappedData);
        break;
    }
  };

  const updateToggleStateForRow = (
    data: { id: string; required: boolean; value: boolean },
    gridIdentifier:
      | NarAgentGridTableColumnKeys.AgentMode
      | NarAgentGridTableColumnKeys.AutopassReview,
  ) => {
    updateToggleState(setTableData, data, gridIdentifier);
  };

  return {
    handleToggleChange,
    updateToggleStateForRow,
    toggleSwitchData,
    setToggleSwitchData,
  };
}
